/**
 * A chart plus its table view.
 *
 * The table is not a fallback nobody opens: it is how a value gets read exactly,
 * how a colour-blind or high-contrast user reads identity, and how the light
 * palette's low-contrast slots are relieved.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Series } from '../api/types';
import { formatDateTime, formatNumber } from '../lib/format';
import { Chart, type ChartPanel } from './Chart';
import { MAX_SERIES } from './palette';

interface Props {
  /** Power/energy series; the first metric of each is plotted. */
  series: Series[];
  metric: string;
  /** Optional second measure (irradiance) — its own panel, never a second axis. */
  irradiance?: Series | null;
  title: string;
  /** Divide by this to convert stored units into the axis unit (W -> kW). */
  scale?: number;
  axisLabel: string;
  digits?: number;
}

export function SeriesView({
  series,
  metric,
  irradiance,
  title,
  scale = 1000,
  axisLabel,
  digits = 1,
}: Props) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);

  const visible = series.slice(0, MAX_SERIES);
  const hidden = series.length - visible.length;

  const panels = useMemo<ChartPanel[]>(() => {
    const main: ChartPanel = {
      axisLabel,
      digits,
      series: visible.map((item, index) => ({
        name: item.label,
        colorIndex: index,
        area: visible.length === 1,
        points: item.points.map((point) => {
          const raw = point.v[metric];
          return [
            new Date(point.t).getTime(),
            typeof raw === 'number' ? raw / scale : null,
          ] as [number, number | null];
        }),
      })),
    };
    const result = [main];
    if (irradiance && irradiance.points.length) {
      result.push({
        axisLabel: t('chart.irradiance'),
        digits: 0,
        series: [
          {
            name: t('kpi.irradiance'),
            // Slot 4 (yellow) for irradiance, kept out of the power series order.
            colorIndex: 3,
            area: true,
            points: irradiance.points.map((point) => {
              const raw = point.v.irradiance_wm2;
              return [
                new Date(point.t).getTime(),
                typeof raw === 'number' ? raw : null,
              ] as [number, number | null];
            }),
          },
        ],
      });
    }
    return result;
  }, [visible, metric, scale, axisLabel, digits, irradiance, t]);

  const timestamps = useMemo(() => {
    const all = new Set<string>();
    for (const item of visible) for (const point of item.points) all.add(point.t);
    return Array.from(all).sort();
  }, [visible]);

  return (
    <figure className="series">
      <figcaption className="series__head">
        <h3>{title}</h3>
        <div className="series__actions">
          {hidden > 0 ? <span className="muted">+{hidden}</span> : null}
          <button type="button" onClick={() => setShowTable((value) => !value)}>
            {showTable ? t('app.chart') : t('app.table')}
          </button>
        </div>
      </figcaption>

      {showTable ? (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr>
                <th scope="col">{t('device.sampleTime')}</th>
                {visible.map((item) => (
                  <th key={item.subject_id} scope="col">
                    {item.label} [{axisLabel}]
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timestamps.slice(-200).map((stamp) => (
                <tr key={stamp}>
                  <th scope="row">{formatDateTime(stamp)}</th>
                  {visible.map((item) => {
                    const point = item.points.find((candidate) => candidate.t === stamp);
                    const raw = point?.v[metric];
                    return (
                      <td key={item.subject_id} className="num">
                        {typeof raw === 'number' ? formatNumber(raw / scale, digits) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Chart panels={panels} ariaLabel={title} height={irradiance ? 420 : 320} />
      )}
    </figure>
  );
}
